import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import DisplayUsers from './components/DisplayUsers';
import {
  fetchOrganisationUsersStart,
  resetUserAdded
} from 'redux/organisation/action';
import { OrganisationUser } from 'redux/organisation/types';
import { DataTableSkeleton, ToastNotification } from 'carbon-components-react';
import {
  selectUsers,
  selectIsModalOpen,
  selectLoadingOrgUsers,
  selectUserAdded
} from 'redux/organisation';
import { currentUser } from 'redux/session/selectors';
import { selectIsLoading } from 'redux/app/selectors';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import AddUser from './components/AddUser';
import { APP } from 'app/AppRoutes';
import { useComponentMount } from 'utilities/hooks.util';

const OrganisationUsers: React.FC<RouteComponentProps<{}, any, any>> = () => {
  const dispatch: any = useDispatch();
  const ref = useRef(null);

  const organisationUsers: Array<OrganisationUser> = useSelector(selectUsers);

  const loadingOrgUsers: boolean = useSelector(selectLoadingOrgUsers);
  const isLoading: boolean = useSelector(selectIsLoading);

  const {
    Organisation: { name, id, role: orgRole } // organisation id of the person logged in
  } = useSelector(currentUser);

  const isModalOpen = useSelector(selectIsModalOpen);

  const userAdded: boolean = useSelector(selectUserAdded);

  useEffect(() => {
    id && dispatch(fetchOrganisationUsersStart(id)); //currentUser.Organisation.id));
  }, [dispatch, id]);

  useEffect(() => {
    const element: any = ref.current;

    if (element) {
      // This is to ensure all element that can be tab, tabindex to -1
      Array.from(element.getElementsByTagName('button')).forEach(
        (item: any) => {
          item.tabIndex = isModalOpen ? -1 : 0;
        }
      );

      Array.from(element.getElementsByTagName('input')).forEach((item: any) => {
        item.tabIndex = isModalOpen ? -1 : 0;
      });

      Array.from(element.getElementsByTagName('a')).forEach((item: any) => {
        item.tabIndex = isModalOpen ? -1 : 0;
      });

      Array.from(element.getElementsByTagName('select')).forEach(
        (item: any) => {
          item.tabIndex = isModalOpen ? -1 : 0;
        }
      );
    }
  }, [isModalOpen]);

  useComponentMount(() => {
    return () => {
      dispatch(resetUserAdded());
    };
  });

  return (
    <div id="OrganisationUsers" data-testid="OrganisationUsers" ref={ref}>
      <FullHeaderLeftSideFullPanelLayout
        pageHeading={`Manage users${
          orgRole !== 'ASSETOWNER' ? ' (administrators)' : ''
        }`}
        pageDescription={`Add or remove ${
          orgRole !== 'ASSETOWNER' ? 'administrators' : 'users'
        } in ${name}`}
        enableHeaderGlobalBar={true}
      >
        {userAdded && (
          <div className="add-org-admin-inline-toast-container">
            <ToastNotification
              key={'success'}
              kind={'success'}
              title={'Success'}
              subtitle={`The new ${
                orgRole !== 'ASSETOWNER' ? 'administrator' : 'user'
              } was created`}
              lowContrast={true}
              hideCloseButton={false}
              onCloseButtonClick={() => dispatch(resetUserAdded())}
              caption={''}
            />
          </div>
        )}
        <div id="members__body-content1" className="members__body-content1">
          {loadingOrgUsers || isLoading || !name ? (
            <DataTableSkeleton showHeader={false} />
          ) : (
            <div id="DisplayUsers" data-testid="DisplayUsers">
              <DisplayUsers
                organisationUsers={organisationUsers}
                data-testid="DisplayUsers"
              />
            </div>
          )}
          {isModalOpen &&
            ReactDOM.createPortal(
              <AddUser />,
              document.querySelector(`#${APP}`) || document.body
            )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};
OrganisationUsers.displayName = 'OrganisationUsers';
export default OrganisationUsers;
